/**
 * geolibre-engine — lazy loader + runner for geolibre-wasm.
 *
 * geolibre-wasm (Qiusheng Wu / opengeos; MIT) is a pure-Rust port of
 * WhiteboxTools compiled to WebAssembly (WASI), exposing 751 geoprocessing
 * tools. This module is the EXECUTION BACKEND for EarthVoice's capability layer
 * (docs/RESEARCH-tool-orchestration.md): student-facing capabilities compile
 * down to runTool() calls here; the LLM never sees the 751 tool ids directly.
 *
 * EXECUTION MOVED OFF THE MAIN THREAD (roadmap H1.4): tool runs happen inside
 * a Web Worker (src/data/geolibre.worker.js) so multi-second geoprocessing
 * (dissolve was 19s pre-CL-123, still seconds) no longer freezes the UI. The
 * public API is unchanged — consumers still call runVectorTool() and get a
 * GeoJSON object back. If the Worker can't be constructed or its first init
 * fails (old browser, CSP), the engine falls back to the original inline
 * main-thread path. A per-call timeout (60s) terminates a wedged worker —
 * WASM calls can't be cancelled cooperatively — and the next call rebuilds it.
 *
 * Loading mirrors duckdb-engine.js: the ~22 MB WASI binary is fetched +
 * compiled on first use (NOT on page load) — now inside the worker — so it
 * never bloats the initial bundle. geolibre's initTools() loads
 * `./geolibre-cli.wasm` relative to the package module, which requires
 * geolibre-wasm to be excluded from Vite's optimizeDeps (see vite.config.js)
 * so that URL is preserved.
 *
 * Vector tools take and return GeoJSON (runVectorTool). Raster tools take and
 * return Cloud-Optimized GeoTIFFs — real-valued rasters, not tiles — via
 * runRasterTool: bytes in, bytes out, with NO GeoJSON encode/decode. The first
 * raster consumers are the Terrain tools (slope / aspect / hillshade) on an
 * ingested DEM COG; the hydrology chain follows.
 */

import { createLogger } from '../utils/logger.js'
import { createWorkerBridge, workerSupported } from './geolibre-worker-protocol.js'

const log = createLogger('geolibre')

// The wasm download + compile can take a while on slow school networks —
// give the one-time init a longer leash than regular tool calls (60s default).
const INIT_TIMEOUT_MS = 120_000

// Single shared promise for the loaded backend (worker bridge or inline
// module). Mirrors the pre-H1.4 single _toolsPromise: avoids racing the init
// on concurrent first calls, and is cleared on failure so a later call can
// retry (e.g. a flaky first WASM fetch).
let _backendPromise = null
let _ready = false
let _statusCb = null
// Set once the Worker path has failed (construction or first init) — all
// subsequent loads go straight to the inline fallback.
let _workerBroken = false

/**
 * Construct the module Worker. The `new URL(..., import.meta.url)` + variable
 * assignment is the Vite idiom: the worker becomes its own build chunk, and
 * the geolibre wasm asset resolves inside it. LAZY — only called on first
 * use, never at module import time (Node/vitest have no Worker global).
 */
function createGeolibreWorker() {
  return new Worker(new URL('./geolibre.worker.js', import.meta.url), { type: 'module' })
}

/** Load the inline (main-thread) backend — the pre-H1.4 path, kept as fallback. */
async function initInlineBackend() {
  const mod = await import('geolibre-wasm/tools')
  await mod.initTools() // browser: fetch + compile ./geolibre-cli.wasm
  return { kind: 'inline', mod }
}

/** Try the Worker backend; on ANY failure mark it broken and fall back inline. */
async function initBackend() {
  if (!_workerBroken && workerSupported()) {
    let bridge = null
    try {
      bridge = createWorkerBridge(createGeolibreWorker)
      await bridge.call({ tool: '__init__' }, { timeoutMs: INIT_TIMEOUT_MS })
      return { kind: 'worker', bridge }
    } catch (err) {
      _workerBroken = true
      bridge?.terminate()
      log.warn('geolibre worker unavailable — falling back to inline execution', err)
    }
  }
  return initInlineBackend()
}

async function backend() {
  if (!_backendPromise) {
    _statusCb?.('loading')
    _backendPromise = (async () => {
      try {
        const be = await initBackend()
        _ready = true
        _statusCb?.('ready')
        log.info(`geolibre tools runner ready (${be.kind})`)
        return be
      } catch (err) {
        // Don't poison the cache: clear the promise so a later call can retry
        // (e.g. a flaky first WASM fetch on a slow school network).
        _backendPromise = null
        _ready = false
        _statusCb?.('error')
        log.error('geolibre tools failed to load', err)
        throw err
      }
    })()
  }
  return _backendPromise
}

/** True once the WASM runner has loaded. Synchronous — safe for a readiness gate. */
export function isGeolibreReady() {
  return _ready
}

/**
 * Register a status callback: receives 'loading' | 'ready' | 'error' as the
 * WASM runner loads. Mirrors duckdb-engine's onStatusChange so the app can
 * track and surface geolibre readiness the same way.
 */
export function setGeolibreStatusHandler(fn) {
  _statusCb = typeof fn === 'function' ? fn : null
}

/**
 * Pre-warm the WASM runner in the background. Fire-and-forget: resolves true on
 * success, false on failure (never rejects), so callers can kick it off on idle
 * without a try/catch. Idempotent — repeated calls reuse the cached load.
 * Since H1.4 this warms the WORKER (spawns it and compiles the wasm inside it).
 */
export function prewarmGeolibre() {
  return backend().then(
    () => true,
    () => false
  )
}

/** Await geolibre readiness (rejects if the load fails). For the cold-start gate. */
export async function waitGeolibreReady() {
  await backend()
}

const _enc = new TextEncoder()
const _dec = new TextDecoder()

/** Encode a GeoJSON object (or string) to the Uint8Array geolibre expects. Pure. */
export function toBytes(geojson) {
  return _enc.encode(typeof geojson === 'string' ? geojson : JSON.stringify(geojson))
}

/** Decode a Uint8Array file back to a GeoJSON object. Pure. */
export function fromBytes(bytes) {
  return JSON.parse(_dec.decode(bytes))
}

/**
 * Choose the GeoJSON output file from a runTool result. Prefers the declared
 * output name, then any *.geojson/*.json file. Pure — exported for testing.
 */
export function pickOutputKey(files, outputName) {
  const keys = Object.keys(files || {})
  if (outputName && keys.includes(outputName)) return outputName
  return keys.find((k) => /\.(geojson|json)$/i.test(k)) || null
}

/**
 * Choose the raster (GeoTIFF) output file from a runTool result. Prefers the
 * declared output name, then any *.tif/*.tiff file. Pure — exported for testing.
 * (The GeoJSON pickOutputKey is wrong for rasters: it looks for *.geojson/*.json.)
 */
export function pickRasterOutputKey(files, outputName = 'output.tif') {
  const keys = Object.keys(files || {})
  if (outputName && keys.includes(outputName)) return outputName
  return keys.find((k) => /\.tiff?$/i.test(k)) || null
}

/** List every available tool id (751). Triggers the WASM load. */
export async function listTools() {
  const be = await backend()
  return be.kind === 'worker' ? be.bridge.call({ tool: '__list_tools__' }) : be.mod.listTools()
}

/** Fetch every tool's parameter manifest — the basis for capability validation. */
export async function listManifests() {
  const be = await backend()
  return be.kind === 'worker'
    ? be.bridge.call({ tool: '__list_manifests__' })
    : be.mod.listManifests()
}

/**
 * Low-level: run one tool over an in-memory filesystem (see geolibre-wasm/tools).
 * `opts.input` values may be Uint8Array/ArrayBuffer (as before) or GeoJSON
 * strings (worker-friendly — encoded off the main thread).
 */
export async function runTool(tool, opts = {}) {
  const { args = [], input = {} } = opts
  const be = await backend()
  if (be.kind === 'worker') {
    // Strings and Uint8Arrays both structured-clone cleanly; the worker
    // encodes strings on its side of the boundary.
    return be.bridge.call({ tool, args, files: input })
  }
  // Inline path: geolibre's own runTool treats plain strings as URLs, so
  // encode GeoJSON strings to bytes here.
  const bytes = {}
  for (const [name, value] of Object.entries(input)) {
    bytes[name] = typeof value === 'string' ? toBytes(value) : value
  }
  return be.mod.runTool(tool, { args, input: bytes })
}

/**
 * High-level vector runner: GeoJSON in → GeoJSON out. Serializes each input
 * FeatureCollection, runs the tool (in the worker when available), and decodes
 * the GeoJSON output.
 *
 * @param {string} tool — e.g. 'spatial_join', 'select_by_location'
 * @param {string[]} args — CLI args, e.g. ['--target=/work/target.geojson', ...]
 * @param {Object<string,object>} inputs — { 'target.geojson': <FeatureCollection>, ... }
 * @param {string} [outputName='out.geojson']
 * @returns {Promise<object>} the output GeoJSON FeatureCollection
 */
export async function runVectorTool(tool, args, inputs = {}, outputName = 'out.geojson') {
  // Serialize to strings: cheap to structured-clone to the worker, which
  // encodes them to bytes off the main thread (inline path encodes here).
  const input = {}
  for (const [name, gj] of Object.entries(inputs)) {
    input[name] = typeof gj === 'string' ? gj : JSON.stringify(gj)
  }

  const { exitCode, stdout, files } = await runTool(tool, { args, input })
  if (exitCode !== 0) {
    throw new Error(`geolibre '${tool}' exited ${exitCode}: ${(stdout || []).join(' ').slice(0, 200)}`)
  }
  const key = pickOutputKey(files, outputName)
  if (!key) {
    throw new Error(
      `geolibre '${tool}': no GeoJSON output (files: ${Object.keys(files).join(', ') || 'none'})`
    )
  }
  return fromBytes(files[key])
}

/**
 * High-level raster runner: GeoTIFF bytes in → GeoTIFF bytes out. The raster
 * analogue of runVectorTool. Unlike the vector path, inputs and the output are
 * RAW raster bytes — the worker protocol forwards Uint8Array untouched, so we
 * must NOT toBytes()-encode inputs (that is GeoJSON-text) nor fromBytes()-decode
 * the output (that JSON-parses). Used by the Terrain tools and, later, hydrology.
 *
 * @param {string} tool — e.g. 'slope', 'aspect', 'hillshade'
 * @param {string[]} args — CLI args, e.g.
 *   ['--input=/work/input.tif','--output=/work/output.tif','--units=degrees']
 * @param {Object<string,Uint8Array>} inputs — { 'input.tif': <COG bytes> }
 * @param {string} [outputName='output.tif']
 * @returns {Promise<Uint8Array>} the output GeoTIFF bytes
 */
export async function runRasterTool(tool, args, inputs = {}, outputName = 'output.tif') {
  const { exitCode, stdout, files } = await runTool(tool, { args, input: inputs })
  if (exitCode !== 0) {
    throw new Error(`geolibre '${tool}' exited ${exitCode}: ${(stdout || []).join(' ').slice(0, 200)}`)
  }
  const key = pickRasterOutputKey(files, outputName)
  if (!key) {
    throw new Error(
      `geolibre '${tool}': no raster output (files: ${Object.keys(files || {}).join(', ') || 'none'})`
    )
  }
  return files[key] // raw GeoTIFF Uint8Array — deliberately NOT fromBytes()
}

/**
 * Fetch a raster's bytes from its (catalog) URL. Full-fetch — fine for the
 * small clipped DEMs of the first Terrain slice; range-request tiling of large
 * COGs is a later optimization.
 *
 * Cache-busts by default: analytic COGs live at fixed R2 keys (e.g.
 * `raster-analytic/<id>.tif`) that get OVERWRITTEN on re-ingest, but `r2.dev`
 * edge-caches the object — a plain fetch keeps returning the stale copy, so a
 * re-ingested DEM (e.g. geographic → UTM) would silently compute on old data.
 * Same fix as the catalog fetch (CL-194). Pass `{ cacheBust: false }` for
 * immutable/unique-key URLs where caching is desirable.
 *
 * Times out (default 60 s — generous for a ≤ MAX_PIXELS DEM on a slow school
 * network) so a stalled download surfaces as a catchable TimeoutError instead
 * of hanging the terrain op forever.
 * @param {string} url
 * @param {{cacheBust?: boolean, timeoutMs?: number}} [opts]
 * @returns {Promise<Uint8Array>}
 */
export async function fetchRasterBytes(url, { cacheBust = true, timeoutMs = 60_000 } = {}) {
  const fetchUrl = cacheBust ? `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}` : url
  const resp = await fetch(fetchUrl, {
    ...(cacheBust ? { cache: 'no-store' } : {}),
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (!resp.ok) throw new Error(`Failed to fetch raster ${url}: HTTP ${resp.status}`)
  return new Uint8Array(await resp.arrayBuffer())
}

/**
 * Dev smoke test — confirms the runner loads and a real tool executes in
 * THIS environment (browser/Vite; worker or inline fallback), with a GeoJSON
 * round-trip. Call from the console:
 * `(await import('/src/data/geolibre-engine.js')).smokeTest()`.
 */
export async function smokeTest() {
  const out = { ok: false }
  try {
    const ids = await listTools()
    out.backend = (await backend()).kind
    out.toolCount = ids.length
    out.hasSpatialJoin = ids.includes('spatial_join')
    out.hasSelectByLocation = ids.includes('select_by_location')

    const fc = (feats) => ({ type: 'FeatureCollection', features: feats })
    const pt = (lng, lat, props = {}) => ({
      type: 'Feature',
      properties: props,
      geometry: { type: 'Point', coordinates: [lng, lat] },
    })
    const result = await runVectorTool(
      'spatial_join',
      [
        '--target=/work/target.geojson',
        '--join=/work/join.geojson',
        '--output=/work/out.geojson',
        '--predicate=intersects',
        '--strategy=first',
      ],
      {
        'target.geojson': fc([pt(0, 0, { name: 'A' })]),
        'join.geojson': fc([pt(0, 0, { tag: 'X' })]),
      }
    )
    out.joinFeatureCount = result?.features?.length ?? null
    out.joinSampleProps = result?.features?.[0]?.properties ?? null
    out.ok = true
  } catch (e) {
    out.error = e?.message || String(e)
  }
  return out
}
